import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { product } from "../content/site";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { BottleVisual } from "../components/BottleVisual";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Shop() {
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifySending, setNotifySending] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    setNotifySending(true);
    try {
      await axios.post(`${API}/notify-signups`, { email: notifyEmail });
      toast.success(
        "Thank you — you're on the list. We'll only contact you about EL Well product availability and launch news."
      );
      setNotifyEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.success(
          "You're already on the list — we'll only contact you about EL Well product availability and launch news."
        );
      } else if (err?.response?.status === 400) {
        toast.error("Please enter a valid email address.");
      } else {
        toast.error("Sorry — that didn't work. Please try again.");
      }
    } finally {
      setNotifySending(false);
    }
  };

  return (
    <div data-testid="shop-page">
      <PageHeader
        eyebrow="The Collection"
        title="One formula, done properly."
        body="EL Well launches with a single product, held to a single standard. New formulas join only when they earn their place."
      />
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <Reveal>
            <div
              data-testid="product-card-magnesium-bisglycinate"
              className="group border border-stone-900/10 bg-[#F3EFE6] transition-colors duration-500 hover:border-stone-900/25"
            >
              <Link to={`/product/${product.slug}`} className="relative flex justify-center overflow-hidden px-8 pb-6 pt-8">
                <BottleVisual className="relative w-52 transition-transform duration-700 ease-out group-hover:-translate-y-2.5" />
              </Link>
              <div className="border-t border-stone-900/10 bg-[#FBF9F5] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-900/80">
                  Nº 01 · 180 Vegan Capsules · 90-Day Supply
                </p>
                <Link to={`/product/${product.slug}`}>
                  <h2 className="mt-2 font-serif text-2xl font-light text-stone-900 transition-colors duration-300 hover:text-emerald-900">
                    {product.name}
                  </h2>
                </Link>
                <div className="mt-5 flex items-center justify-between">
                  <p data-testid="shop-product-price" className="font-serif text-2xl font-light text-stone-900">
                    £{product.price.toFixed(2)}
                  </p>
                  <span
                    data-testid="shop-add-to-bag"
                    className="cursor-not-allowed bg-stone-900/40 px-5 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-stone-50/80"
                  >
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="product-card-coming-soon"
              className="flex h-full min-h-[420px] flex-col items-center justify-center border border-dashed border-stone-900/25 p-10 text-center"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Nº 02</p>
              <p className="mt-4 font-serif text-3xl font-light text-stone-400">In development</p>
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-stone-500">
                Our next formula is being considered. Leave your email and we will write to you about product
                availability and launch news — nothing else.
              </p>
              <form onSubmit={submitNotify} data-testid="notify-form" className="mt-8 w-full max-w-xs">
                <input
                  type="email"
                  required
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  data-testid="notify-email-input"
                  placeholder="you@example.co.uk"
                  aria-label="Email address for product notifications"
                  className="w-full border border-stone-900/20 bg-transparent px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 transition-colors duration-300 focus:border-emerald-900 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="notify-submit-button"
                  disabled={notifySending}
                  className="mt-3 w-full border border-stone-900 px-7 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50 disabled:opacity-50"
                >
                  {notifySending ? "Joining…" : "Join the Waiting List"}
                </button>
                <p className="mt-3 text-[11px] leading-relaxed text-stone-400">
                  Product availability and launch news only — no unrelated marketing.
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
